// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Button from '@mui/material/Button';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import CustomSnackbar from '../ui/Snackbars';
import EditIcon from '@mui/icons-material/Edit';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import React, { useCallback, useEffect, useState } from 'react';
import SelectVisaDialog from '../visa/SelectVisaDialog.js';
import Tooltip from '@mui/material/Tooltip';
import useHttp from '../../utils/http';
import wso2Theme from '../../theme';
import { adjustForTimezone, getCountryNameFromCountryCode, getDateForDB } from '../../utils/formatting';
import { patchTravelAgent, patchTravelStatus, patchTravelVisa } from '../../utils/end-points';
import { getTravelStatusColor, getTravelStatusFontColor } from "../../theme/status-colors"
import { isAccessGranted } from "../../utils/oauth";
import { NO_VALID_VISA_AVAILABLE_ID, VISA_NOT_APPLICABLE_ID } from '../../Config';
import { PRIVILEGES } from "../../Config.js";
import { travelStatus, travelStatusArray } from "../../utils/const.js"
import "../../App.css";
import "../../css/TravelInformation.scss"
import FundingSources from './FundingSources';

// const costFieldsArray = [
//     { id: 1, fieldLabel: "Ground Cost", costType: "ground", currencyLabel: "ground_currency", costLabel: "ground_cost" },
//     { id: 2, fieldLabel: "Change Fee", costType: "change", currencyLabel: "change_currency", costLabel: "change_cost" },
//     { id: 3, fieldLabel: "Other Expenses", costType: "other", currencyLabel: "other_currency", costLabel: "other_cost" }
// ]

const TravelInformation = (props) => {

    const { travel, visa, travelAgents, fetchData, isLoading, setIsLoading } = props
    const { handleRequest } = useHttp()
    const [filteredTravelStatus, setFilteredTravelStatus] = useState(null)
    const [selectedTravelAgent, setSelectedTravelAgent] = useState(null)
    const [selectedVisa, setSelectedVisa] = useState(null)
    const [selectedTravelStatus, setSelectedTravelStatus] = useState("")
    const [isResetEnable, setResetEnable] = useState(false)
    const [isTravelStatusEditable, setTravelStatusEditable] = useState(false)
    const [isTravelHandlerEditable, setTravelHandlerEditable] = useState(false)
    const [isTravelStatusChanged, setIsTravelStatusChanged] = useState(false)
    const [isTravelAgentChanged, setIsTravelAgentChanged] = useState(false)
    const [isVisaChanged, setIsVisaChanged] = useState(false)
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const [visaDialog, setVisaDialog] = useState(false)
    const [confirmationDialogData, setConfirmationDialogData] = useState({ open: false, title: "", content: "", acceptLabel: "", declineLabel: "", acceptAlert: null, declineAlert: null })

    // const [currencies, setCurrencies] = useState({
    //     flight: travel.flight_currency,
    //     hotel: travel.hotel_currency,
    //     visa: travel.visa_currency,
    //     ground: travel.ground_currency,
    //     change: travel.change_currency,
    //     other: travel.other_currency
    // })

    // const [costs, setCosts] = useState({
    //     flight: travel.flight_cost,
    //     hotel: travel.hotel_cost,
    //     visa: travel.visa_cost,
    //     ground: travel.ground_cost,
    //     change: travel.change_cost,
    //     other: travel.other_cost
    // })

    useEffect(() => {
        initTravelData()
    }, [travel.visaId, travel.travelAgentId, travel.status])

    useEffect(() => {
        if (isTravelStatusEditable || isTravelHandlerEditable) {
            setResetEnable(true)
        }
    }, [isTravelStatusEditable, isTravelHandlerEditable])

    const initTravelData = () => {
        if (travel.visaId) {
            let fetchedVisa = visa.filter(visa => visa.id == travel.visaId)
            if (fetchedVisa.length > 0) {
                fetchedVisa = fetchedVisa[0]
                var visaObject = {}

                var startDate = adjustForTimezone(new Date(travel.startDate))
                var endDate = adjustForTimezone(new Date(travel.endDate))

                var visaStartDate = adjustForTimezone(new Date(fetchedVisa.startDate))
                var visaEndDate = adjustForTimezone(new Date(fetchedVisa.endDate))

                if (visaStartDate <= startDate && visaEndDate >= endDate) {
                    visaObject = { id: fetchedVisa.id, label: getCountryNameFromCountryCode(fetchedVisa.country) + " - " + fetchedVisa.visaType + " - " + fetchedVisa.entryType + " - (" + getDateForDB(adjustForTimezone(new Date(fetchedVisa.startDate))) + " to " + getDateForDB(adjustForTimezone(new Date(fetchedVisa.endDate))) + ")", status: "valid" }
                }
                else {
                    visaObject = { id: fetchedVisa.id, label: getCountryNameFromCountryCode(fetchedVisa.country) + " - " + fetchedVisa.visaType + " - " + fetchedVisa.entryType + " - (" + getDateForDB(adjustForTimezone(new Date(fetchedVisa.startDate))) + " to " + getDateForDB(adjustForTimezone(new Date(fetchedVisa.endDate))) + ")", status: "expired" }
                }
                setSelectedVisa(visaObject)
            }
            else if (travel.visaId === NO_VALID_VISA_AVAILABLE_ID.id) {
                var visaObject = {}
                visaObject = { id: NO_VALID_VISA_AVAILABLE_ID.id, label: NO_VALID_VISA_AVAILABLE_ID.label }
                setSelectedVisa(visaObject)
            }
            else if (travel.visaId === VISA_NOT_APPLICABLE_ID.id) {
                var visaObject = {}
                visaObject = { id: VISA_NOT_APPLICABLE_ID.id, label: VISA_NOT_APPLICABLE_ID.label }
                setSelectedVisa(visaObject)
            }
            else {
                setSelectedVisa(null)
            }
        }
        else {
            setSelectedVisa(null)
        }
        if (travel.travelAgentId) {
            let travelAgent = travelAgents.filter(agent => agent.id == travel.travelAgentId)
            if (travelAgent.length > 0) {
                setSelectedTravelAgent(travelAgent[0])
            }
        }
        // TODO : uncomment these  lines
        if (travel.status) {
            setSelectedTravelStatus(travel.status)
            switch (travel.status) {
                case travelStatus.NEW: setFilteredTravelStatus(travelStatusArray.filter(status => (status === travelStatus.NEW || status === travelStatus.INPROGRESS || status === travelStatus.ONHOLD || status === travelStatus.CANCELLED))); break;
                case travelStatus.INPROGRESS: setFilteredTravelStatus(travelStatusArray.filter(status => (status === travelStatus.INPROGRESS || status === travelStatus.COMPLETED || status === travelStatus.ONHOLD || status === travelStatus.CANCELLED || status === travelStatus.EXTENDED))); break;
                case travelStatus.COMPLETED: setFilteredTravelStatus(travelStatusArray.filter(status => (status === travelStatus.COMPLETED || status === travelStatus.EXTENDED || status === travelStatus.CANCELLED))); break;
                case travelStatus.ONHOLD: setFilteredTravelStatus(travelStatusArray.filter(status => (status === travelStatus.INPROGRESS || status === travelStatus.ONHOLD || status === travelStatus.CANCELLED || status === travelStatus.EXTENDED))); break;
                case travelStatus.CANCELLED: setFilteredTravelStatus(travelStatusArray.filter(status => (status === travelStatus.INPROGRESS || status === travelStatus.ONHOLD || status === travelStatus.CANCELLED || status === travelStatus.EXTENDED))); break;
                case travelStatus.EXTENDED: setFilteredTravelStatus(travelStatusArray.filter(status => (status === travelStatus.ONHOLD || status === travelStatus.CANCELLED || status === travelStatus.EXTENDED || status === travelStatus.COMPLETED))); break;
                default: setFilteredTravelStatus([]); break;
            }
        }

        // TODO : remove these 2 lines
        // setSelectedTravelStatus(travel.status)
        // setFilteredTravelStatus(travelStatusArray)

        setIsLoading(false)

    }


    // const getCurrencyValue = (type) => {
    //     switch (type) {
    //         case "flight":
    //             return currencies.flight
    //         case "hotel":
    //             return currencies.hotel
    //         case "visa":
    //             return currencies.visa
    //         case "ground":
    //             return currencies.ground
    //         case "change":
    //             return currencies.change
    //         case "other":
    //             return currencies.other
    //         default:
    //             return ""
    //     }
    // }


    // const getCostValue = (type) => {
    //     switch (type) {
    //         case "flight":
    //             return costs.flight
    //         case "hotel":
    //             return costs.hotel
    //         case "visa":
    //             return costs.visa
    //         case "ground":
    //             return costs.ground
    //         case "change":
    //             return costs.change
    //         case "other":
    //             return costs.other
    //         default:
    //             return ""
    //     }
    // }

    const handleTravelAgentChange = (event) => {
        setSelectedTravelAgent(event.target.value)
        setResetEnable(true)
        setIsTravelAgentChanged(true)
    };

    const handleTravelStatusChange = (event) => {
        setSelectedTravelStatus(event.target.value)
        setResetEnable(true)
        setIsTravelStatusChanged(true)
    };

    const getVisaOptions = () => {
        var visaOptions = []
        var filteredVisa = visa

        var startDate = adjustForTimezone(new Date(travel.startDate))
        var endDate = adjustForTimezone(new Date(travel.endDate))

        filteredVisa.map(visa => {
            var visaStartDate = adjustForTimezone(new Date(visa.startDate))
            var visaEndDate = adjustForTimezone(new Date(visa.endDate))
            if (visaStartDate <= startDate && visaEndDate >= endDate) {
                var obj = { id: visa.id, label: getCountryNameFromCountryCode(visa.country) + " - " + visa.visaType + " - " + visa.entryType + " - (" + getDateForDB(adjustForTimezone(new Date(visa.startDate))) + " to " + getDateForDB(adjustForTimezone(new Date(visa.endDate))) + ")", status: "valid" }
                visaOptions.push(obj)
            }
        })

        visaOptions.push({ id: NO_VALID_VISA_AVAILABLE_ID.id, label: NO_VALID_VISA_AVAILABLE_ID.label })
        visaOptions.push({ id: VISA_NOT_APPLICABLE_ID.id, label: VISA_NOT_APPLICABLE_ID.label })

        // filteredVisa.map(visa => {
        //     var visaStartDate = adjustForTimezone(new Date(visa.startDate))
        //     var visaEndDate = adjustForTimezone(new Date(visa.endDate))

        //     if (!(visaStartDate <= startDate && visaEndDate >= endDate)) {
        //         var obj = { id: visa.id, label: getCountryNameFromCountryCode(visa.country) + " - " + visa.visaType + " - " + visa.entryType + " - (" + getDateForDB(adjustForTimezone(new Date(visa.startDate))) + " to " + getDateForDB(adjustForTimezone(new Date(visa.endDate))) + ")", status: "expired" }
        //         visaOptions.push(obj)
        //     }
        // })

        return visaOptions
    }

    const resetForm = () => {

        setConfirmationDialogData({
            open: true,
            title: "Reset Travel Details",
            content: "Are you sure you want reset travel information?",
            acceptLabel: "Yes",
            declineLabel: "No",
            acceptAlert: handleAcceptResetAlert,
            declineAlert: handleDeclineAlert
        })
    }

    const handleAcceptResetAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
        initTravelData()
        setIsTravelAgentChanged(false)
        setIsTravelStatusChanged(false)
        setIsVisaChanged(false)
        setResetEnable(false)
        setTravelStatusEditable(false)
        setTravelHandlerEditable(false)
    }

    const handleDeclineAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
    }

    const saveChanges = () => {

        // Validate if travel status, travel agent, visa are not empty
        if (selectedTravelStatus === travelStatus.INPROGRESS || selectedTravelStatus === travelStatus.COMPLETED | selectedTravelStatus === travelStatus.EXTENDED) {
            if (!selectedTravelAgent && !selectedVisa) {
                setSnackbarData({ message: "Please select a Travel Agent and a Visa", open: true, severity: "error", onClose: onCloseSnackbar })
                return
            }
            if (!selectedTravelAgent) {
                setSnackbarData({ message: "Please select a Travel Agent", open: true, severity: "error", onClose: onCloseSnackbar })
                return
            }
            if (!selectedVisa) {
                setSnackbarData({ message: "Please select a Visa", open: true, severity: "error", onClose: onCloseSnackbar })
                return
            }
        }

        setConfirmationDialogData({
            open: true,
            title: "Update Travel Details",
            content: "Are you sure you want to update travel information?",
            acceptLabel: "Yes",
            declineLabel: "No",
            acceptAlert: updateTravelInformation,
            declineAlert: handleDeclineAlert
        })

        // validateTravelInformation()

    }

    // const validateTravelInformation = () => {


    //     // Validate travel information to make the request as completed
    //     if (selectedTravelStatus === travelStatus.COMPLETED) {
    //         setIsLoading(true)
    //         handleRequest(validateTravelInformationByJobNumber + "?jobNumber=" + travel.jobNumber, "GET", null, (data) => {
    //             setIsLoading(false)
    //             if (data.isValidationError) {
    //                 setSnackbarData({ message: data.validationErrorMessage, open: true, severity: "warning", onClose: onCloseSnackbar })
    //             }
    //             else {
    //                 setConfirmationDialogData({
    //                     open: true,
    //                     title: "Update Travel Details",
    //                     content: "Are you sure you want to update travel information?",
    //                     acceptLabel: "Yes",
    //                     declineLabel: "No",
    //                     acceptAlert: updateTravelInformation,
    //                     declineAlert: handleDeclineAlert
    //                 })
    //             }
    //         }, (error) => {
    //             setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
    //         })
    //     }
    //     else {
    //         setConfirmationDialogData({
    //             open: true,
    //             title: "Update Travel Details",
    //             content: "Are you sure you want to update travel information?",
    //             acceptLabel: "Yes",
    //             declineLabel: "No",
    //             acceptAlert: updateTravelInformation,
    //             declineAlert: handleDeclineAlert
    //         })
    //     }
    // }

    const updateTravelInformation = () => {

        setConfirmationDialogData(data => ({ ...data, open: false }))

        setIsLoading(true)
        const promises = []

        if (isTravelStatusChanged) {
            const body = {
                jobNumber: travel.jobNumber,
                status: (selectedTravelStatus ? selectedTravelStatus : travel.status)
            }

            promises.push(new Promise(function (resolve, reject) {
                handleRequest(patchTravelStatus, "PATCH", body, (data) => {
                    if (data.status === body.status) {
                        resolve();
                    }
                    else {
                        setSnackbarData({ message: "Travel Status Update Failed", open: true, severity: "error", onClose: onCloseSnackbar })
                        reject()
                    }
                }, (error) => {
                    setIsLoading(false)
                    setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                })
            }))
        }

        if (isTravelAgentChanged) {
            const body = {
                jobNumber: travel.jobNumber,
                travelAgentId: (selectedTravelAgent && selectedTravelAgent.id) ? selectedTravelAgent.id : travel.travelAgentId
            }

            promises.push(new Promise(function (resolve, reject) {
                handleRequest(patchTravelAgent, "PATCH", body, (data) => {
                    if (data.travelAgentId === body.travelAgentId) {
                        resolve()
                    }
                    else {
                        setSnackbarData({ message: "Travel Agent Update Failed", open: true, severity: "error", onClose: onCloseSnackbar })
                        reject()
                    }
                }, (error) => {
                    setIsLoading(false)
                    setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                })
            }))
        }

        if (isVisaChanged) {
            const body = {
                jobNumber: travel.jobNumber,
                visaId: (selectedVisa && selectedVisa.id) ? selectedVisa.id : travel.visa
            }

            promises.push(new Promise(function (resolve, reject) {
                handleRequest(patchTravelVisa, "PATCH", body, (data) => {
                    if (data.visaId === body.visaId) {
                        resolve()
                    }
                    else {
                        setSnackbarData({ message: "Visa Update Failed", open: true, severity: "error", onClose: onCloseSnackbar })
                        reject()
                    }
                }, (error) => {
                    setIsLoading(false)
                    setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                })
            }))
        }

        Promise.all(promises)
            .then(() => {
                // sendEmails(isTravelStatusChange)

                setIsTravelAgentChanged(false)
                setIsTravelStatusChanged(false)
                setIsVisaChanged(false)
                setResetEnable(false)
                setTravelStatusEditable(false)
                setTravelHandlerEditable(false)

                fetchData(true)
                setSnackbarData({ message: "Success", open: true, severity: "success", onClose: onCloseSnackbar })

            })
            .catch((error) => {
                setSnackbarData({ message: "Failed", open: true, severity: "error", onClose: onCloseSnackbar })
                console.error(error)
            })
    }

    // TODO: Next release
    // const sendEmails = (isTravelStatusChange) => {
    //     let emailType = ""
    //     if (selectedTravelStatus === travelStatus.INPROGRESS && travel.status !== travelStatus.INPROGRESS) {
    //         emailType = "NEWREQUEST"
    //     }
    //     else if (selectedTravelStatus === travelStatus.CANCELLED && travel.status === travelStatus.INPROGRESS) {
    //         emailType = "CANCELLED"
    //     }
    //     else if (selectedTravelStatus === travelStatus.COMPLETED && travel.status === travelStatus.INPROGRESS) {
    //         emailType = "COMPLETED"
    //     }
    //     else if ((selectedTravelStatus === travelStatus.INPROGRESS || selectedTravelStatus === travelStatus.EXTENDED) && travel.travelAgentId !== selectedTravelAgent.id) {
    //         emailType = "TRAVELAGENTCHANGE"
    //     }
    //     else if ((selectedTravelStatus === travelStatus.INPROGRESS || selectedTravelStatus === travelStatus.EXTENDED) && travel.visaId !== selectedVisa.id) {
    //         emailType = "VISACHANGE"
    //     }

    //     if (emailType) {
    //         const body = {
    //             jobNumber: travel.jobNumber,
    //             visaId: (selectedVisa && selectedVisa.id) ? selectedVisa.id : travel.visa,
    //             status: selectedTravelStatus,
    //             travelAgentId: selectedTravelAgent ? selectedTravelAgent.id : null,
    //             oldTravelAgentId: travel.travelAgentId,
    //             visaId: selectedVisa ? selectedVisa.id : null,
    //             emailType: emailType
    //         }

    //         handleRequest(postTravelEmail, "POST", body, (data) => {
    //             if (data.emailResult) {
    //                 setIsTravelAgentChanged(false)
    //                 setIsTravelStatusChanged(false)
    //                 setIsVisaChanged(false)
    //                 setResetEnable(false)
    //                 setTravelStatusEditable(false)
    //                 setTravelHandlerEditable(false)
    //                 if (isTravelStatusChange) {
    //                     fetchData(true)
    //                 }
    //                 else {
    //                     setSnackbarData({ message: "Success", open: true, severity: "success", onClose: onCloseSnackbar })
    //                 }
    //             }
    //             else {
    //                 setSnackbarData({ message: "Email Sending Failed", open: true, severity: "error", onClose: onCloseSnackbar })
    //             }
    //             setIsLoading(false)
    //         }, (error) => {
    //             setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
    //         })
    //     }
    //     else {
    //         setIsTravelAgentChanged(false)
    //         setIsTravelStatusChanged(false)
    //         setIsVisaChanged(false)
    //         setResetEnable(false)
    //         setTravelStatusEditable(false)
    //         setTravelHandlerEditable(false)
    //         setIsLoading(false)
    //         if (isTravelStatusChange) {
    //             fetchData(true)
    //         }
    //         else {
    //             setSnackbarData({ message: "Success", open: true, severity: "success", onClose: onCloseSnackbar })
    //         }
    //     }

    // }

    const handleCloseVisaDialog = (isSubmit, selectedVisa) => {
        setVisaDialog(false);
        if (isSubmit) {
            setSelectedVisa(selectedVisa)
            setResetEnable(true)
            setIsVisaChanged(true)
        }
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (
        <div className='Travel-information'>

            <ConfirmationDialog confirmationDialogData={confirmationDialogData} />
            <CustomSnackbar snackbar={snackbarData} />

            <SelectVisaDialog open={visaDialog} onClose={handleCloseVisaDialog} selectedVisaValue={selectedVisa} email={travel.travellerEmail} options={getVisaOptions()} />


            <div>
                <Grid container>
                    <Grid item lg={12} md={12}>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Travel Status : </Grid>
                                    <Grid item lg={5} md={5} sm={5}>
                                        {isTravelStatusEditable ?
                                            <FormControl fullWidth size="small" >
                                                <Select
                                                    value={selectedTravelStatus} onChange={handleTravelStatusChange}
                                                    required>
                                                    {filteredTravelStatus.length > 0 && filteredTravelStatus.map(status => {
                                                        return <MenuItem key={status} value={status}>
                                                            <span className="Status-dot-dropdown" style={{ backgroundColor: getTravelStatusColor(status) }}></span>{status}
                                                        </MenuItem>
                                                    })}
                                                </Select>
                                            </FormControl> :
                                            <span className='Status-chip'
                                                style={{
                                                    backgroundColor: getTravelStatusColor(travel.status),
                                                    color: getTravelStatusFontColor(travel.status)
                                                }}>
                                                {travel.status}
                                            </span>
                                        }
                                        {isAccessGranted([PRIVILEGES.EDIT_TRAVEL_STATUS]) && !isTravelStatusEditable && <Tooltip title="Edit" placement="bottom">
                                            <EditIcon className='Edit-icon'
                                                style={{ color: wso2Theme.palette.secondary.main }}
                                                onClick={() => setTravelStatusEditable(!isTravelStatusEditable)} />
                                        </Tooltip>}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Job Number : </Grid>
                                    <Grid item lg={8} md={7} sm={6}>{travel.jobNumber}</Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Traveller's Email : </Grid>
                                    <Grid item lg={8} md={7} sm={6}>{travel.travellerEmail}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Base Location : </Grid>
                                    <Grid item lg={8} md={7} sm={6} >{travel.employeeLocation}</Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Engagement Id : </Grid>
                                    <Grid item lg={8} md={7} sm={6} >{travel.engagementId}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Engagement Code : </Grid>
                                    <Grid item lg={8} md={7} sm={6} >{travel.engagementCode}</Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Engagement Type : </Grid>
                                    <Grid item lg={8} md={7} sm={6} >{travel.engagementType}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Customer : </Grid>
                                    <Grid item lg={8} md={7} sm={6} >{travel.customerName}</Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Country : </Grid>
                                    <Grid item lg={8} md={7} sm={6}>{travel.country}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Travel Location : </Grid>
                                    <Grid item lg={8} md={7} sm={6}>{travel.city}</Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Start Date : </Grid>
                                    <Grid item lg={8} md={7} sm={6}>{getDateForDB(adjustForTimezone(new Date(travel.startDate)))}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>End Date : </Grid>
                                    <Grid item lg={8} md={7} sm={6}>{getDateForDB(adjustForTimezone(new Date(travel.endDate)))}</Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Travel Agent : </Grid>
                                    <Grid item lg={5} md={5} sm={5}>
                                        {isTravelHandlerEditable ?
                                            <FormControl fullWidth size="small">
                                                <Select value={selectedTravelAgent} onChange={handleTravelAgentChange} required>
                                                    {travelAgents && travelAgents.length > 0 && travelAgents.filter(agent => agent.status === 'Active').map(agent => {
                                                        return <MenuItem key={agent.id} value={agent}>{agent.nameOfTravelAgent}</MenuItem>
                                                    })}
                                                </Select>
                                            </FormControl> :
                                            <span>{selectedTravelAgent ? selectedTravelAgent.nameOfTravelAgent : ""}</span>
                                        }
                                        {isAccessGranted([PRIVILEGES.EDIT_TRAVEL_AGENT]) && !isTravelHandlerEditable && <Tooltip title="Edit" placement="bottom">
                                            <span>
                                                <IconButton onClick={() => setTravelHandlerEditable(!isTravelHandlerEditable)} size="small" className='Edit-icon' disabled={!(selectedTravelStatus === travelStatus.INPROGRESS || selectedTravelStatus === travelStatus.EXTENDED)}>
                                                    <EditIcon style={{ color: !(selectedTravelStatus === travelStatus.INPROGRESS || selectedTravelStatus === travelStatus.EXTENDED) ? wso2Theme.palette.disabled.main : wso2Theme.palette.secondary.main }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item lg={6} md={6} sm={12}>
                                <Grid container className='Travel-info-entry'>
                                    <Grid item lg={4} md={5} sm={6} className='Travel-info-span'>Visa : </Grid>
                                    <Grid item lg={8} md={7} sm={6}>
                                        <span>{selectedVisa ? selectedVisa.label : (travel.visa ? travel.visa : "")}</span>
                                        {isAccessGranted([PRIVILEGES.EDIT_TRAVEL_VISA]) && <Tooltip title="Edit" placement="bottom">
                                            <span>
                                                <IconButton onClick={() => setVisaDialog(true)} size="small" className='Edit-icon' disabled={!(selectedTravelStatus === travelStatus.INPROGRESS || selectedTravelStatus === travelStatus.EXTENDED)}>
                                                    <EditIcon style={{ color: !(selectedTravelStatus === travelStatus.INPROGRESS || selectedTravelStatus === travelStatus.EXTENDED) ? wso2Theme.palette.disabled.main : wso2Theme.palette.secondary.main }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {isAccessGranted([PRIVILEGES.VIEW_FUNDING_SOURCES]) &&
                            <Grid container className='Travel-info-entry Funding-sources-div'>
                                <Grid item lg={2} className='Travel-info-span'>Funding Sources : </Grid>
                                <Grid item lg={10}>
                                    <FundingSources allocationId={travel.allocationId} />
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                    <React.Fragment>

                        {/* {(selectedTravelStatus != travelStatus.NEW) && (

                            <Grid item lg={4} className='Cost-grid-item'>
                                {costFieldsArray.map((item, key) => {
                                    return <div key={key}>
                                        <div className='Cost-label'>{item.fieldLabel} : </div>
                                        <Grid container className='Cost-grid-container'>
                                            <Grid item lg={4}>
                                                <FormControl fullWidth size="small" disabled={!isAccessGranted([PRIVILEGES.EDIT_TRAVEL_REQUEST])}>
                                                    <Select value={getCurrencyValue(item.costType) ? getCurrencyValue(item.costType) : ""} >
                                                        {currencyTypes && currencyTypes.length > 0 && currencyTypes.map(currency => {
                                                            return <MenuItem key={currency[0]} value={currency[0]}>{currency[0]}</MenuItem>
                                                        })}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item lg={8} className="Cost-amount">
                                                <TextField value={getCostValue(item.costType) ? getCostValue(item.costType) : ""} variant='outlined' size='small' fullWidth 
                                                    disabled={!isAccessGranted([PRIVILEGES.EDIT_TRAVEL_REQUEST])}/>
                                            </Grid>
                                        </Grid>
                                    </div>

                                })}
                            </Grid>
                        )} */}

                    </React.Fragment>
                </Grid>
                {isAccessGranted([PRIVILEGES.EDIT_TRAVEL_AGENT, PRIVILEGES.EDIT_TRAVEL_STATUS, PRIVILEGES.EDIT_TRAVEL_VISA]) &&
                    <div className='Button-group'>
                        <Button variant="contained" size="small" color="secondary" className='Save-button' disabled={!(isTravelAgentChanged || isTravelStatusChanged || isVisaChanged)} onClick={saveChanges}>Save</Button>
                        <Button variant="contained" size="small" className='Reset-button' disabled={!isResetEnable} onClick={resetForm}>Reset</Button>
                    </div>
                }
            </div>
        </div >
    );
}

export default TravelInformation;
